import type {
  TwoFactorErrorInfo} from '../../services/two-factor.service';
import {
  TwoFactorError,
  TwoFactorService,
  TwoFactorSetupType,
  TwoFAResult
} from '../../services/two-factor.service';
import { getVerificationErrorMessage, TwoFactorMethodSetupVerifyComponent } from '../two-factor-method-setup-verify/two-factor-method-setup-verify.component';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { I18nService } from '@ppl/i18n';
import { ButtonType } from '@ppl/ui/button';
import type { TFormGroup } from '@ppl/ui/form';
import { TFormBuilder } from '@ppl/ui/form';
import { wrapAPICall } from '@ppl/utils/api.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import { assertNever } from '@ppl/utils/typescript.utils';
import type {
  Observable} from 'rxjs';
import {
  BehaviorSubject,
  concat,
  defer,
  EMPTY,
  of,
  Subject,
  throwError
} from 'rxjs';
import {
  delay,
  map,
  switchMap,
  tap
} from 'rxjs/operators';


@Component({
    selector: 'ppl-two-factor-method-setup-common',
    templateUrl: './two-factor-method-setup-common.component.html',
    styleUrls: ['./two-factor-method-setup-common.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TwoFactorMethodSetupCommonComponent {

  @Input() step = TwoFactorMethodSetupCommonStep.INIT;
  @Input() methodType: TwoFactorSetupType;
  @Input() securityToken: string;
  @Input() getSetupData: () => TwoFactorMethodSetupCommonSetupData;
  @Input() title: string;

  @Input() formGroup: UntypedFormGroup;
  @Input() loading = false;
  @Input() actionDisabled = false;
  @Input() showResendCode = true;

  @Input() width = '460px';

  @Output() nextStep = new EventEmitter<TwoFactorMethodSetupCommonInitData>();
  @Output() finish = new EventEmitter<TwoFactorMethodSetupCommonFinishData>();
  @Output() cancel = new EventEmitter();

  init$ = new BehaviorSubject<boolean>(false);
  verifying$ = new BehaviorSubject<boolean>(false);

  verifyForm: TFormGroup<TwoFactorMethodSetupCommonVerifyFormData>;

  initData: TwoFactorMethodSetupCommonInitData;
  resendSetupCode$ = new Subject();
  resendSetupToggleSentMessage$ = this.resendSetupCode$.pipe(
    switchMap(() => {
      return concat(of(true), of(false).pipe(delay(HIDE_MESSAGE_AFTER_TIME)));
    })
  );

  error$ = new BehaviorSubject<TwoFactorErrorInfo>(null);

  TwoFactorError = TwoFactorError;
  TwoFactorMethodSetupCommonStep = TwoFactorMethodSetupCommonStep;

  get actionLabel() {
    switch (this.step) {
      case TwoFactorMethodSetupCommonStep.VERIFY:
        return this.i18nService.translate('Verify');
      default:
        return this.i18nService.translate('Continue');
    }
  }

  get actionButtonType() {
    return this.step === TwoFactorMethodSetupCommonStep.VERIFY
      ? ButtonType.Button4
      : ButtonType.Button2;
  }

  get codeFormControl() {
    return this.verifyForm.get('code');
  }

  @MemoizeLast<TwoFactorMethodSetupCommonComponent>(['step', 'formGroup'])
  get form() {
    return this.step === TwoFactorMethodSetupCommonStep.VERIFY
      ? this.verifyForm
      : this.formGroup || null;
  }

  constructor(
    private i18nService: I18nService,
    private fb: TFormBuilder,
    private twoFactorService: TwoFactorService
  ) {
    this.verifyForm = this.fb.group<TwoFactorMethodSetupCommonVerifyFormData>(
      TwoFactorMethodSetupVerifyComponent.getFormControlConfig()
    );
  }

  onAction() {
    switch (this.step) {
      case TwoFactorMethodSetupCommonStep.INIT: {
        this.init(this.getSetupData()).subscribe(setupData => {
          this.nextStep.emit(setupData);
        });
      } break;
      case TwoFactorMethodSetupCommonStep.VERIFY: {
        if (this.verifyForm.valid) {
          const { code } = this.verifyForm.value;
          this.verify(code).subscribe({
            next: response => {
              switch (response.result) {
                case TwoFAResult.EXPIRED:
                case TwoFAResult.FAILED:
                  this.verifyForm.get('code').setErrors({
                    custom: getVerificationErrorMessage(this.i18nService, response.result)
                  });
                  break;
                case TwoFAResult.FINISHED:
                  this.finish.emit({ methodId: response.id });
                  break;
                case TwoFAResult.IN_PROGRESS:
                  break;
                default:
                  assertNever(response.result);
              }
            }
          });
        }
      } break;
      case TwoFactorMethodSetupCommonStep.CUSTOM:
        this.nextStep.emit(this.initData);
        break;
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  onResendCode() {
    if (this.initData) {
      const { setupData } = this.initData;
      this.init(setupData).subscribe(() => {
        this.resendSetupCode$.next();
      });
    }
  }

  getRetryAfterMessage(error: TwoFactorErrorInfo) {
    return this.twoFactorService.getRetryAfterMessage(error.retryAfter);
  }

  init(data: TwoFactorMethodSetupCommonSetupData) {
    return this.callAPI(
      this.twoFactorService.setupStart({
        type: this.methodType,
        securityToken: this.securityToken,
        ...data
      }),
      this.init$
    ).pipe(
      map(response => {
        const initData: TwoFactorMethodSetupCommonInitData = {
          setupData: data,
          key: response.key,
          setupToken: response.setup_token
        };
        return initData;
      }),
      tap(initData => {
        this.initData = initData;
      })
    );
  }

  verify(code: string) {
    if (this.initData) {
      const { setupToken } = this.initData;
      return this.callAPI(
        this.twoFactorService.setupVerify({ type: this.methodType, setupToken, code }),
        this.verifying$
      );
    } else {
      return EMPTY;
    }
  }

  private callAPI<T>(observable$: Observable<T>, progress$: BehaviorSubject<boolean>): Observable<T> {
    return defer(() => {
      this.error$.next(null);
      return wrapAPICall(observable$, progress$).pipe(
        this.twoFactorService.handleError({
          errorHandlerFn: (errorInfo, originalError) => {
            if (errorInfo.error !== TwoFactorError.INVALID_OR_EXPIRED_TOKEN) {
              this.error$.next(errorInfo);
            }
            return errorInfo.error === TwoFactorError.UNKNOWN
              ? throwError(originalError)
              : EMPTY;
          }
        })
      );
    });
  }
}

const HIDE_MESSAGE_AFTER_TIME = 5000;

export enum TwoFactorMethodSetupCommonStep {
  INIT,
  CUSTOM,
  VERIFY
}

export interface TwoFactorMethodSetupCommonSetupData {
  value?: string;
  name?: string;
}

export interface TwoFactorMethodSetupCommonInitData {
  setupData: TwoFactorMethodSetupCommonSetupData;
  setupToken: string;
  key: string | null;
}

export interface TwoFactorMethodSetupCommonFinishData {
  methodId: number;
}

interface TwoFactorMethodSetupCommonVerifyFormData {
  code: string;
}
